import { useCallback, useEffect, useRef, useState } from "react";
import { onMilestone } from "../net/popSocket";
import { playFanfare } from "../audio/pops";
import { ensureAudio } from "../audio/AudioEngine";
import { useSettingsStore } from "../state/useSettingsStore";
import { dbg } from "../lib/debug";
import { copy } from "../content/copy";

// Site-wide milestone celebration. Mounted once in App, above the routes, so it
// fires wherever the visitor happens to be standing — and because the server
// announces the crossing to every socket at once, everybody sees it together.

const DURATION_MS = 6_000;
const PIECES = 150;
const SEEN_KEY = "poparium.celebrated";

const CONFETTI: [number, number, number][] = [
  [255, 62, 165],
  [53, 224, 255],
  [255, 225, 77],
  [255, 122, 89],
  [124, 92, 255],
  [92, 255, 192],
];

type Piece = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  spin: number;
  w: number;
  h: number;
  color: string;
};

/** Thresholds already celebrated for this visitor, so a refresh doesn't replay. */
function alreadySeen(threshold: number): boolean {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? (JSON.parse(raw) as number[]).includes(threshold) : false;
  } catch {
    return false;
  }
}

function markSeen(threshold: number): void {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const list = raw ? (JSON.parse(raw) as number[]) : [];
    if (!list.includes(threshold)) list.push(threshold);
    localStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-40)));
  } catch {
    /* private mode — the celebration just isn't deduped across reloads */
  }
}

export default function Celebration() {
  const [active, setActive] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const celebrate = useCallback((threshold: number, options?: { force?: boolean }) => {
    if (!options?.force && alreadySeen(threshold)) return;
    markSeen(threshold);
    dbg.lastMilestone = threshold;
    setActive(threshold);
    // The context is already unlocked if they've popped anything; if not this
    // is a no-op and the celebration is simply silent, which is correct.
    ensureAudio();
    playFanfare();
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setActive(null), DURATION_MS);
  }, []);

  useEffect(() => {
    const off = onMilestone((threshold) => celebrate(threshold));
    // Preview hook: /?celebrate=1000 replays the moment without waiting for the
    // world to actually get there. Used by scripts/milestone-check.mjs.
    const preview = new URLSearchParams(location.search).get("celebrate");
    if (preview) {
      const n = Number(preview);
      if (Number.isFinite(n) && n > 0) celebrate(n, { force: true });
    }
    return () => {
      off();
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [celebrate]);

  useEffect(() => {
    if (active === null) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (useSettingsStore.getState().reducedMotion) return; // banner only

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const pieces: Piece[] = Array.from({ length: PIECES }, () => {
      const [r, g, b] = CONFETTI[Math.floor(Math.random() * CONFETTI.length)];
      return {
        x: Math.random() * w,
        y: -20 - Math.random() * h * 0.6,
        vx: (Math.random() - 0.5) * 90,
        vy: 120 + Math.random() * 220,
        rot: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 7,
        w: 7 + Math.random() * 7,
        h: 10 + Math.random() * 10,
        color: `rgb(${r},${g},${b})`,
      };
    });

    let raf = 0;
    let last = performance.now();
    const started = last;

    const tick = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const life = (now - started) / DURATION_MS;
      ctx.clearRect(0, 0, w, h);
      if (life >= 1) {
        cancelAnimationFrame(raf);
        return;
      }
      const fade = life > 0.75 ? 1 - (life - 0.75) / 0.25 : 1;

      for (const p of pieces) {
        p.vy += 190 * dt;
        p.vx *= 1 - 0.4 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.spin * dt;
        if (p.y > h + 40) continue;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = fade;
        ctx.fillStyle = p.color;
        // Squash on spin so the pieces read as tumbling foil, not blocks.
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.rot)));
        ctx.restore();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  if (active === null) return null;

  return (
    <div className="celebration" role="status" aria-live="polite">
      <canvas className="celebration-canvas" ref={canvasRef} aria-hidden="true" />
      <div className="celebration-banner">
        <p className="celebration-kicker">{copy.milestone.kicker}</p>
        <p className="celebration-number">{active.toLocaleString("en-US")}</p>
        <p className="celebration-sub">{copy.milestone.sub}</p>
      </div>
    </div>
  );
}
